import * as React from 'react';
import { type RootState, useSelector } from '../../state';
import { type Person, selectors } from '../../state/editor';
import { nameFallback } from '../../utils';
import { msgsToComponents } from '../../utils/msgs-to-components';

const msgs = {
  BoardSingle: ({ name }) => (
    <>
      Du bestemmer selv hvor stort styret skal være. Foreløpig består det av{' '}
      <b>{name}</b> som styreleder og eneste medlem.
    </>
  ),
  BoardMultiple: ({ chairName, members }) => (
    <>
      Du bestemmer selv hvor stort styret skal være. Foreløpig består det av{' '}
      <b>{chairName}</b> som styreleder og {members}.
    </>
  ),
};

const Msg = msgsToComponents(msgs);

function boardProse(chair: Person, boardMembers: Person[]) {
  if (boardMembers.length === 0) {
    return boardSingularProse(chair);
  } else {
    return boardPluralProse(chair, boardMembers);
  }
}

function boardSingularProse(person: Person) {
  return (
    <p>
      <Msg.BoardSingle name={nameFallback(person.name)} />
    </p>
  );
}

function boardPersonListing(people: Person[]) {
  if (people.length === 1) {
    return (
      <>
        {' '}
        <strong>{nameFallback(people[0].name)}</strong> som styremedlem
      </>
    );
  } else {
    const andPart = people.length - 2;
    return (
      <>
        {people.map((p, n) => {
          return (
            // Using a fragment leads to a Chrome bug where there is no space between after the last name
            // Use span instead for now.
            <span key={p.id}>
              <strong>{nameFallback(p.name)}</strong>
              {n === people.length - 1 ? '' : andPart === n ? ' og ' : ', '}
            </span>
          );
        })}{' '}
        som styremedlemmer
      </>
    );
  }
}

function boardPluralProse(chair: Person, boardMembers: Person[]) {
  return (
    <p>
      <Msg.BoardMultiple
        chairName={nameFallback(chair.name)}
        members={boardPersonListing(boardMembers)}
      />
    </p>
  );
}

function deputiesProse(deputies: Person[]) {
  if (deputies.length === 0) {
    return null;
  } else if (deputies.length === 1) {
    return (
      <p>
        <strong>{nameFallback(deputies[0].name)}</strong> er varamedlem i
        styret.
      </p>
    );
  } else {
    const andPart = deputies.length - 2;
    return (
      <>
        {deputies.map((p, n) => {
          return (
            <span key={p.id}>
              <strong>{nameFallback(p.name)}</strong>
              {n === deputies.length - 1 ? '' : andPart === n ? ' og ' : ', '}
            </span>
          );
        })}{' '}
        er varamedlemmer i styret.
      </>
    );
  }
}

function selector(state: RootState) {
  return {
    chair: selectors.getChair(state),
    boardMembers: selectors.getBoardMember(state),
    deputies: selectors.getDeputy(state),
  };
}

export const BoardMemberSummary: React.FC = () => {
  const { chair, boardMembers, deputies } = useSelector(selector);
  return (
    <>
      {boardProse(chair, boardMembers)}
      {deputiesProse(deputies)}
    </>
  );
};
